import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PopupModal.css';

const POPUP_STORAGE_KEY = 'popup_data';
const BASE_URL = process.env.BASE_URL;
const FETCH_TIMEOUT_MS = 8000;

const PopupModal = () => {
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const showTimerRef = useRef(null);
  const imageLoadTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const IMAGE_LOAD_TIMEOUT_MS = 15000;

  const shouldShowPopup = useCallback((popupData) => {
    if (typeof window === 'undefined') return false;
    if (!popupData || !popupData.id) return false;

    const storageKey = `${POPUP_STORAGE_KEY}_${popupData.id}`;

    if (popupData.frequency === 'every_visit') {
      const shownThisSession = sessionStorage.getItem(storageKey);
      return !shownThisSession;
    }

    const stored = localStorage.getItem(storageKey);
    if (!stored) return true;

    try {
      const { timestamp } = JSON.parse(stored);

      switch (popupData.frequency) {
        case 'once':
          return false;
        case 'every_x_days':
          const daysPassed = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
          const daysRequired = popupData.frequency_days || 1;
          return daysPassed >= daysRequired;
        default:
          return true;
      }
    } catch (error) {
      return true;
    }
  }, []);

  const markPopupShown = useCallback((popupId, frequency) => {
    if (typeof window === 'undefined') return;

    const storageKey = `${POPUP_STORAGE_KEY}_${popupId}`;

    if (frequency === 'every_visit') {
      sessionStorage.setItem(storageKey, Date.now().toString());
      return;
    }

    const data = {
      timestamp: Date.now(),
      shownAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(data));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    mountedRef.current = true;

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), FETCH_TIMEOUT_MS);

    const initialTimer = setTimeout(() => {
      const currentUrl = window.location.href;
      const url = `${BASE_URL}/popups/active?page=${encodeURIComponent(currentUrl)}`;

      fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      })
        .then((response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .then((data) => {
          clearTimeout(timeoutId);
          if (!mountedRef.current) return;
          if (!data?.success || !data?.data) return;
          const popupData = data.data;
          if (!shouldShowPopup(popupData)) return;

          const delay =
            popupData.delay_type === 'instant'
              ? 0
              : (popupData.delay_seconds || 0) * 1000;

          showTimerRef.current = setTimeout(() => {
            if (!mountedRef.current) return;
            setImageLoaded(false);
            setPopup(popupData);
            setIsVisible(true);
            markPopupShown(popupData.id, popupData.frequency);
          }, delay);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          if (error?.name !== 'AbortError') {
            console.error('[PopupModal] Error fetching popup:', error);
          }
        });
    }, 150);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeoutId);
      abortController.abort();
      clearTimeout(initialTimer);
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [shouldShowPopup, markPopupShown]);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !popup) return;
    imageLoadTimeoutRef.current = setTimeout(() => {
      setImageLoaded(true);
      imageLoadTimeoutRef.current = null;
    }, IMAGE_LOAD_TIMEOUT_MS);
    return () => {
      if (imageLoadTimeoutRef.current) {
        clearTimeout(imageLoadTimeoutRef.current);
        imageLoadTimeoutRef.current = null;
      }
    };
  }, [isVisible, popup]);

  const handleClose = () => {
    if (imageLoadTimeoutRef.current) {
      clearTimeout(imageLoadTimeoutRef.current);
      imageLoadTimeoutRef.current = null;
    }
    setIsVisible(false);
    setImageLoaded(false);
  };

  const handleImageLoad = useCallback(() => {
    if (imageLoadTimeoutRef.current) {
      clearTimeout(imageLoadTimeoutRef.current);
      imageLoadTimeoutRef.current = null;
    }
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    if (imageLoadTimeoutRef.current) {
      clearTimeout(imageLoadTimeoutRef.current);
      imageLoadTimeoutRef.current = null;
    }
    setImageLoaded(true);
  }, []);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible || !popup) {
    return null;
  }

  return (
    <div 
      className="popup-overlay" 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
    >
      <div className="popup-container">
        <button
          type="button"
          onClick={handleClose}
          className="popup-close-btn"
          aria-label="Cerrar popup"
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        
        <div className="popup-content">
          {!imageLoaded && <div className="popup-image-placeholder" aria-hidden="true" />}
          {popup.link ? (
            <a
              href={popup.link}
              target={popup.link_new_tab ? '_blank' : '_self'}
              rel={popup.link_new_tab ? 'noopener noreferrer' : undefined}
              onClick={handleClose}
              className="popup-link"
            >
              <img
                src={popup.image}
                alt={popup.name || 'Promoción'}
                className="popup-image"
                loading="eager"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </a>
          ) : (
            <img
              src={popup.image}
              alt={popup.name || 'Promoción'}
              className="popup-image"
              loading="eager"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}
        </div>
        
        <span id="popup-title" className="sr-only">
          {popup.name || 'Popup promocional'}
        </span>
      </div>
    </div>
  );
};

export default PopupModal;
